import { useState } from 'react';

import {
  useButtonPressedCallback,
  useClickstreamCallback
} from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';

import { EIN_ANSWER } from '#client/constants/ein';

import { FormControlLabelStyled } from './components/FormControlLabel.styles';

import { EIN_INVALID_MESSAGE } from './constants/errorMessages';

export const EinUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  data,
  dataChangeHandler = () => {},
  disabled
}) => {
  const [values, setValues] = useState({
    ein: data?.ein ?? '',
    pick: data?.einAnswer,
    valid: true,
    isDirty: false,
    isFocus: false
  });

  const isShowError = values.isDirty && !values.valid && !values.isFocus;

  const trackEinChangeClick = useButtonPressedCallback({
    elementText: 'I have an EIN',
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });
  const onDataChanged = (lastValues) => {
    if (lastValues.valid) {
      field.setLoading();

      if (lastValues.pick === EIN_ANSWER.HAVE_EIN) {
        /**
         * Tracking Details: Tracking to be added to the banking application Collect Info screen.
         * Question:
         *          Where are customers typically dropping off on this screen?
         *          Do they typically drop off at SSN?
         *          How often are they viewing the user agreements?
         */
        trackEinChangeClick({
          elementText: 'I have an EIN',
          elementName: 'banking_collect_info',
          appPageName: 'banking_application'
        });
      }
    }

    // Only include ein key if user has selected "I have an EIN"
    const dataFields = {
      einAnswer: lastValues.einAnswer
    };

    if (lastValues.pick === EIN_ANSWER.HAVE_EIN) {
      dataFields.ein = lastValues.ein;
    }

    dataChangeHandler(
      lastValues.valid,
      dataFields,
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError, lastValues.valid);
        forceUpdate();
      },
      field.key
    );
  };

  const trackEinPickChangeClick = useClickstreamCallback('buttonPressed');
  const handlePickChange = (event) => {
    const newValue = event.target.value;
    const newValues = {
      ...values,
      pick: newValue,
      ...(newValue === EIN_ANSWER.WILL_BUY_EIN && {
        einAnswer: EIN_ANSWER.WILL_BUY_EIN,
        ein: '',
        valid: true
      }),
      ...(newValue === EIN_ANSWER.WAITING_EIN && {
        einAnswer: EIN_ANSWER.WAITING_EIN,
        ein: '',
        valid: true
      }),
      ...(newValue === EIN_ANSWER.HAVE_EIN && {
        einAnswer: EIN_ANSWER.HAVE_EIN,
        ein: '',
        valid: false
      })
    };
    setValues(newValues);
    onDataChanged(newValues);
    /**
     * Tracking Details: Tracking to be added to the banking application Collect Info screen.
     * Question:
     *          Where are customers typically dropping off on this screen?
     *          Do they typically drop off at SSN?
     *          How often are they viewing the user agreements?
     */
    trackEinPickChangeClick({
      elementText: {
        willBuyEIN: 'I need an EIN',
        waitingEIN: 'I’ll upload my EIN',
        haveEIN: 'I have an EIN'
      }[newValue],
      elementName: 'banking_collect_info',
      appPageName: 'banking_application'
    });
  };

  const handleEinChange = (event) => {
    const newValue = event.target.value;
    const newValues = {
      ...values,
      valid: /^\d{9}$/.test(newValue),
      ein: newValue
    };
    setValues(newValues);
    onDataChanged(newValues);
  };

  const inputNumberOnly = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <Stack spacing="16px">
      <FormControl disabled={disabled}>
        <RadioGroup
          name="ein"
          onChange={handlePickChange}
          sx={{ gap: '24px' }}
          value={values.pick}
        >
          <FormControlLabel
            control={<Radio />}
            label={<Text size="xl">I need an EIN from ZenBusiness.</Text>}
            value={EIN_ANSWER.WILL_BUY_EIN}
          />
          <FormControlLabelStyled
            control={<Radio />}
            label={
              <Text size="xl">
                I’ll upload my EIN when I get it from the IRS. (We’ll send a reminder, so we can
                submit your banking application.)
              </Text>
            }
            value={EIN_ANSWER.WAITING_EIN}
          />
          <FormControlLabel
            control={<Radio />}
            label={<Text size="xl">I have an EIN and can provide it now.</Text>}
            value={EIN_ANSWER.HAVE_EIN}
          />
        </RadioGroup>
      </FormControl>
      {values.pick === EIN_ANSWER.HAVE_EIN && (
        <Stack paddingLeft="32px">
          <FormControl
            disabled={disabled}
            fullWidth
            variant="outlined"
          >
            <TextField
              disabled={disabled}
              error={isShowError}
              id="ein"
              inputProps={{
                maxLength: 9,
                inputMode: 'numeric'
              }}
              label="Employer Identification Number"
              onBlur={() => setValues({ ...values, isDirty: true, isFocus: false })}
              onChange={handleEinChange}
              onFocus={() => setValues({ ...values, isFocus: true })}
              onKeyPress={inputNumberOnly}
              // Use text input instead of number input to prevent the display up/down arrows
              type="text"
              value={values.ein}
            />
          </FormControl>
          {isShowError && (
            <Text
              color="red"
              component="div"
              pt="8px"
              size="xs"
            >
              {EIN_INVALID_MESSAGE}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

EinUIField.propTypes = {
  data: PropTypes.shape({
    ein: PropTypes.string,
    einAnswer: PropTypes.string
  }).isRequired,
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool
};
